import React, { useState, useRef, useEffect } from "react";
import { Link } from "gatsby"; // Import Link component from Gatsby
import {
  bgStyle,
  boxShadow,
  colorWhite,
  colorBlack,
  uppercase,
  borderight,
  borderightBlack,
  barlow,
} from "./communityHeader.module.scss";
import Menu from "./communityMenu.js";

const CommunityHeader = ({
  cname,
  phone,
  address,
  hours, // Fallback hours
  hoursData, // New hours data
  slug,
  email,
  actOveride = false,
  emergencyStatus = false,
  emergencyHeight = 0,
  area,
  citySlug,
  onMenuStateChange,
}) => {
  const [active, setActive] = useState(false);
  const [scroll, setScroll] = useState(actOveride);
  const [showHours, setShowHours] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isVirtual, setIsVirtual] = useState(false);
  const [closedAt, setClosedAt] = useState("");
  const [opensAt, setOpensAt] = useState("");
  const [hoursContent, setHoursContent] = useState(""); // For display
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  const [style, setStyle] = useState({});
  const [linkIt, setLinkIt] = useState(
    `/apartments/${area.toLowerCase()}/${citySlug.toLowerCase()}/${slug.toLowerCase()}/`,
  );

  // Manually adjust cname for line breaks
  if (cname.includes("Winter Park")) {
    cname = cname.replace("Winter Park", "Winter\u00A0Park");
  }

  useEffect(() => {
    let style =
      !scroll & !active
        ? {
            background:
              "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.50) 50%, rgba(0,0,0,0) 100%)",
          }
        : {
            background: "#fefefe",
            color: "#5C6369",
            borderBottom: "1px solid rgba(0,0,0,0.3)",
          };
    if (emergencyStatus && !scroll) {
      style = { ...style, top: `${emergencyHeight}px` };
    }
    setStyle(style);
  }, [emergencyStatus, emergencyHeight, scroll, active]);

  const handleScroll = () => {
    if (window.scrollY > 0 && !scroll) {
      setScroll(true);
    } else if (window.scrollY == 0 && scroll) {
      setScroll(actOveride);
    }
  };

  const handleShowHours = () => {
    setShowHours(!showHours);
  };

  const handleSetActive = (b) => {
    setActive(b);
    if (onMenuStateChange) {
      onMenuStateChange(b);
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
    }
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scroll]);

  const ref = useRef();

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setActive(false);
        if (onMenuStateChange) {
          onMenuStateChange(false);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {
    const hoursToUse = hoursData || (hours ? hours.split("\n") : null);

    if (hoursToUse) {
      const hoursText = hoursToUse.join("\n");
      setHoursContent(hoursText);

      // Split hours into sections to handle virtual hours
      const sections = hoursText.split("\n\n");
      const baseHoursArray = sections[0].split("\n");

      let virtualHoursArray = [];
      if (sections.length > 1 && sections[1].startsWith("__Virtual Hours__")) {
        virtualHoursArray = sections[1]
          .replace("__Virtual Hours__", "")
          .trim()
          .split("\n");
      }

      const {
        isOpen: openStatus,
        isVirtual: virtualStatus,
        opensAt: openTime,
        closedAt: closeTime,
      } = parseHours(baseHoursArray, virtualHoursArray);

      setIsOpen(openStatus);
      setIsVirtual(virtualStatus);
      setOpensAt(openTime);
      setClosedAt(closeTime);
    }
  }, [hoursData, hours]);


  const parseHours = (baseHoursArray, virtualHoursArray) => {
    const parseTime = (timeString) => {
      if (typeof timeString !== "string") {
        console.error("Invalid input for parseTime. Expected a string but received:", timeString);
        return null;
      }
      const regex = /(\d{1,2}(?::\d{2})?)\s*(AM|PM)$/i;
      const match = timeString.match(regex);
      if (match) {
        const time = match[1];
        const period = match[2].toLowerCase();
        const [hours, minutes] = time.includes(":")
          ? time.split(":")
          : [time, "00"];
        let hoursParsed = parseInt(hours, 10);
        if (period === "pm" && hoursParsed !== 12) {
          hoursParsed += 12;
        } else if (period === "am" && hoursParsed === 12) {
          hoursParsed = 0;
        }
        return new Date(
          `2000-01-01T${hoursParsed.toString().padStart(2, "0")}:${minutes}:00`,
        );
      }
      return null;
    };

    const now = new Date();
    const currentDayIndex = now.getDay(); // 0 (Sun) to 6 (Sat)
    const currentTimeParsed = new Date(
      `2000-01-01T${now.getHours().toString().padStart(2, "0")}:${now
        .getMinutes()
        .toString()
        .padStart(2, "0")}:00`,
    );

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    let isOpen = false;
    let isVirtual = false;
    let opensAt = null;
    let closedAt = null;

    // Combine base and virtual hours into a map for easier lookup
    const hoursMap = {};
    baseHoursArray.forEach((line) => {
      const [dayPart, ...rest] = line.split(" ");
      const day = dayPart.trim();
      const times = rest.join(" ").trim();
      hoursMap[day] = { times, virtual: false };
    });
    virtualHoursArray.forEach((line) => {
      const [dayPart, ...rest] = line.split(" ");
      const day = dayPart.trim();
      const times = rest.join(" ").trim();
      hoursMap[day] = { times, virtual: true };
    });

    // Iterate over the next 7 days starting from today
    for (let i = 0; i < 7; i++) {
      const dayIndex = (currentDayIndex + i) % 7;
      const day = daysOfWeek[dayIndex];
      const dayInfo = hoursMap[day];

      if (dayInfo) {
        const { times, virtual } = dayInfo;
        if (times && times !== "Closed" && times !== "By Appointment") {
          const [startStr, endStr] = times.split(" - ");
          const startTime = parseTime(startStr);
          const endTime = parseTime(endStr);

          if (startTime && endTime) {
            const stn = startTime.getTime();
            const etn = endTime.getTime();

            if (i === 0) {
              // Today
              const ctn = currentTimeParsed.getTime();
              if (ctn >= stn && ctn <= etn) {
                // Currently open
                isOpen = true;
                isVirtual = virtual;
                closedAt = endStr;
                break;
              } else if (ctn < stn) {
                // Opens later today
                opensAt = startStr;
                isVirtual = virtual;
                break;
              }
              // Else, current time is after closing time today; continue to next day
            } else {
              // Future day
              opensAt = `${startStr} on ${day}`;
              isVirtual = virtual;
              break;
            }
          }
        }
      }
    }

    return {
      isOpen,
      isVirtual,
      opensAt,
      closedAt,
    };
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 1024);
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  return (
    <section
      className={
        bgStyle +
        " " +
        boxShadow +
        " navbar is-fixed-top is-transparent is-vcentered"
      }
      id="cpage"
      style={style}
      ref={ref}
    >
      <div className="navbar-brand do-not-translate">
        <h1
          className={
            (!scroll && !active ? borderight : borderightBlack) +
            " " +
            uppercase +
            " subtitle is-size-3"
          }
          style={{
            postition: "relative",
            minWidth: "228px",
            marginRight: "1rem",
          }}
        >
          <Link
            to={linkIt}
            className={
              (!scroll && !active ? colorWhite : colorBlack) + " navbar-item"
            }
          >
            {cname}
          </Link>
          <Link to="/">
            {!scroll && !active ? (
              <img
                src="/imgs/whitemcklogo.png"
                alt="A McKinley Property"
                style={{
                  maxWidth: "200px",
                  position: "absolute",
                  left: "3.5rem",
                  bottom: "0",
                }}
              />
            ) : (
              <img
                src="/imgs/mckproplogo.png"
                alt="A McKinley Property"
                id="scrolled_mckinley_logo"
                style={{
                  maxWidth: "179px",
                  position: "absolute",
                  left: "4rem",
                  bottom: "1.4rem",
                }}
              />
            )}
          </Link>
        </h1>
      </div>
      <Menu
        address={address}
        scroll={scroll}
        active={active}
        email={email}
        opensAt={opensAt}
        handleShowHours={handleShowHours}
        isOpen={isOpen}
        isVirtual={isVirtual}
        closedAt={closedAt}
        showHours={showHours}
        hours={hoursContent}
        slug={slug}
        phone={phone}
        htmlId={isMobile ? "mobile_menu" : ""}
        isMobile={isMobile}
        handleSetActive={handleSetActive}
        linkIt={linkIt}
        communityName={cname}
      />
    </section>
  );
};

export default CommunityHeader;
